<script setup>
import { ref, computed } from 'vue';

// Shows which Facebook/Instagram ad (or post) a conversation came from, built
// from the Click-to-WhatsApp / click-to-Messenger `referral` captured at message
// ingestion and enriched with the campaign name + spend by
// AdReferralEnrichmentJob. Only rendered when `ad_referral` is present on the
// conversation, so it never shows for organically-started chats.
const props = defineProps({
  referral: {
    type: Object,
    required: true,
  },
});

const isPost = computed(() => props.referral.type === 'post');

const title = computed(
  () =>
    props.referral.campaign_name ||
    props.referral.ad_name ||
    props.referral.headline ||
    'إعلان'
);

const body = computed(
  () => props.referral.ad_text || props.referral.body || ''
);
const spend = computed(() => props.referral.spend);
const adUrl = computed(() => props.referral.url);
const postUrl = computed(() => props.referral.post_url);

// Headline metric: how much each conversation costs on this ad (spend ÷
// conversations started) — matches Ads Manager's Cost per result.
const costPerConversation = computed(
  () => props.referral.cost_per_conversation
);
const conversationsStarted = computed(
  () => props.referral.conversations_started
);

// The ad's welcome-flow questions + prepared answers (reconstructed from the ad).
const welcomeFaqs = computed(() =>
  Array.isArray(props.referral.welcome_faqs) ? props.referral.welcome_faqs : []
);
const faqsOpen = ref(false);

// Arabic copy is kept here (not in the template) to match the dashboard's
// convention and to satisfy the no-bare-strings lint rule.
const sourceLabel = computed(() =>
  isPost.value ? '🎯 جه من منشور' : '🎯 جه من إعلان'
);
const costLabel = '💰 تكلفة الرسالة في الإعلان';
const spendLabel = 'صرف:';
const conversationsLabel = 'محادثة';
const dotLabel = '·';
const currencyLabel = 'جنيه';
const adLinkLabel = 'شوف الإعلان';
const postLinkLabel = 'شوف المنشور';
const faqsToggleLabel = computed(
  () => `❓ أسئلة وإجابات الإعلان (${welcomeFaqs.value.length})`
);
const faqsArrow = computed(() => (faqsOpen.value ? '▾' : '▸'));
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 mx-2 mt-2 border border-n-weak rounded-lg bg-n-alpha-2"
  >
    <div class="flex items-center gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">
        {{ sourceLabel }}
      </span>
    </div>

    <!-- Headline: how much each conversation costs on this ad -->
    <div
      v-if="costPerConversation"
      class="flex flex-col gap-1 px-3 py-2.5 border rounded-xl bg-n-brand/10 border-n-brand"
    >
      <span class="text-xs font-semibold text-n-slate-11" dir="auto">
        {{ costLabel }}
      </span>
      <p class="flex items-baseline gap-1.5" dir="auto">
        <span
          class="text-2xl font-extrabold leading-none text-n-brand"
          dir="ltr"
        >
          {{ costPerConversation }}
        </span>
        <span class="text-sm font-bold text-n-brand">
          {{ currencyLabel }}
        </span>
      </p>
      <p v-if="spend" class="text-xs text-n-slate-11" dir="auto">
        {{ spendLabel }} <span dir="ltr">{{ spend }}</span> {{ currencyLabel }}
        <template v-if="conversationsStarted">
          {{ dotLabel }} <span dir="ltr">{{ conversationsStarted }}</span>
          {{ conversationsLabel }}
        </template>
      </p>
    </div>

    <p class="text-sm font-medium leading-snug text-n-slate-12" dir="auto">
      {{ title }}
    </p>

    <p
      v-if="body"
      class="text-xs leading-relaxed text-n-slate-11 line-clamp-3 whitespace-pre-line"
      dir="auto"
    >
      {{ body }}
    </p>

    <!-- The ad's welcome flow: the questions the customer saw + their answers -->
    <div v-if="welcomeFaqs.length" class="flex flex-col">
      <button
        type="button"
        class="flex items-center gap-1 text-xs font-medium text-start text-n-brand"
        @click="faqsOpen = !faqsOpen"
      >
        {{ faqsToggleLabel }}
        <span dir="ltr">{{ faqsArrow }}</span>
      </button>
      <div v-if="faqsOpen" class="flex flex-col gap-2 mt-2">
        <div
          v-for="(faq, i) in welcomeFaqs"
          :key="i"
          class="flex flex-col gap-0.5 ps-2 border-s-2 border-n-weak"
        >
          <p
            v-if="faq.q"
            class="text-xs font-semibold text-n-slate-12"
            dir="auto"
          >
            {{ faq.q }}
          </p>
          <p
            v-if="faq.a"
            class="text-xs leading-relaxed text-n-slate-11 whitespace-pre-line"
            dir="auto"
          >
            {{ faq.a }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mt-1">
      <a
        v-if="adUrl"
        :href="adUrl"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="text-xs text-n-brand"
      >
        {{ adLinkLabel }}
      </a>
      <a
        v-if="postUrl"
        :href="postUrl"
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="text-xs text-n-brand"
      >
        {{ postLinkLabel }}
      </a>
    </div>
  </div>
</template>
